import {
  ApiBlueprint,
  configApiRef,
  createFrontendModule,
  discoveryApiRef,
  DiscoveryApi,
  fetchApiRef,
  FetchApi,
  identityApiRef,
  storageApiRef,
} from '@backstage/frontend-plugin-api';
import { CatalogClient } from '@backstage/catalog-client';
import { ApiEntity } from '@backstage/catalog-model';
import {
  analyticsApiRef,
  gitlabAuthApiRef,
  googleAuthApiRef,
  microsoftAuthApiRef,
  oktaAuthApiRef,
  oneloginAuthApiRef,
} from '@backstage/frontend-plugin-api';
import {
  ScmAuth,
  ScmAuthApi,
  ScmAuthTokenOptions,
  ScmAuthTokenResponse,
  ScmIntegrationsApi,
  scmAuthApiRef,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import {
  DefaultEntityPresentationApi,
  DefaultStarredEntitiesApi,
} from '@backstage/plugin-catalog';
import {
  apiDocsConfigRef,
  defaultDefinitionWidgets,
} from '@backstage/plugin-api-docs';
import {
  catalogApiRef,
  entityPresentationApiRef,
  starredEntitiesApiRef,
} from '@backstage/plugin-catalog-react';
import {
  DEFAULT_FORMATTER_NAME,
  getDefaultFormatters,
  KubernetesAuthProviders,
  KubernetesBackendClient,
  KubernetesClusterLinkFormatter,
  kubernetesApiRef,
  kubernetesAuthProvidersApiRef,
  kubernetesClusterLinkFormatterApiRef,
  KubernetesProxyClient,
  kubernetesProxyApiRef,
} from '@backstage/plugin-kubernetes-react';
import { SearchQuery, SearchResultSet } from '@backstage/plugin-search-common';
import { SearchApi, searchApiRef } from '@backstage/plugin-search-react';
import {
  GitlabCIApiRef,
  GitlabCIClient,
} from '@immobiliarelabs/backstage-plugin-gitlab';
import { DrivelineUsageAnalyticsApi } from './UsageAnalyticsApi';

const isRejectedAuthError = (error: unknown) => {
  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.name === 'RejectedError' ||
    error.message.includes('Login failed, rejected by user')
  );
};

const rejectedGitlabToken = 'driveline-gitlab-auth-rejected';

const createGitlabScmAuth = (
  gitlabAuthApi: Parameters<typeof ScmAuth.forGitlab>[0],
): ScmAuthApi => {
  const scmAuth = ScmAuth.forGitlab(gitlabAuthApi);

  return {
    async getCredentials(
      options: ScmAuthTokenOptions,
    ): Promise<ScmAuthTokenResponse> {
      try {
        return await scmAuth.getCredentials(options);
      } catch (error) {
        if (isRejectedAuthError(error)) {
          return { headers: {}, token: rejectedGitlabToken };
        }

        throw error;
      }
    },
  };
};

const appendSearchParam = (
  params: URLSearchParams,
  key: string,
  value: unknown,
) => {
  if (value === undefined || value === null) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      appendSearchParam(params, `${key}[${index}]`, item),
    );
    return;
  }

  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(
      ([childKey, childValue]) =>
        appendSearchParam(params, `${key}[${childKey}]`, childValue),
    );
    return;
  }

  params.append(key, String(value));
};

const stringifySearchQuery = (query: SearchQuery) => {
  const params = new URLSearchParams();

  appendSearchParam(params, 'term', query.term ?? '');
  appendSearchParam(params, 'types', query.types);
  appendSearchParam(params, 'filters', query.filters);
  appendSearchParam(params, 'pageLimit', query.pageLimit);
  appendSearchParam(params, 'pageCursor', query.pageCursor);

  return params.toString();
};

class DrivelineSearchClient implements SearchApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  async query(
    query: SearchQuery,
    options?: { signal?: AbortSignal },
  ): Promise<SearchResultSet> {
    const baseUrl = await this.options.discoveryApi.getBaseUrl('search');
    const response = await this.options.fetchApi.fetch(
      `${baseUrl}/query?${stringifySearchQuery(query)}`,
      { signal: options?.signal },
    );

    if (!response.ok) {
      throw new Error(
        `Search query failed with ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }
}

const searchApi = ApiBlueprint.make({
  name: 'search',
  params: defineParams =>
    defineParams({
      api: searchApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new DrivelineSearchClient({ discoveryApi, fetchApi }),
    }),
});

const analyticsApi = ApiBlueprint.make({
  name: 'analytics',
  params: defineParams =>
    defineParams({
      api: analyticsApiRef,
      deps: {
        configApi: configApiRef,
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ configApi, discoveryApi, fetchApi }) =>
        new DrivelineUsageAnalyticsApi({ configApi, discoveryApi, fetchApi }),
    }),
});

const scmIntegrationsApi = ApiBlueprint.make({
  name: 'scm-integrations',
  params: defineParams =>
    defineParams({
      api: scmIntegrationsApiRef,
      deps: { configApi: configApiRef },
      factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
    }),
});

const scmAuthApi = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams =>
    defineParams({
      api: scmAuthApiRef,
      deps: { gitlabAuthApi: gitlabAuthApiRef },
      factory: ({ gitlabAuthApi }) => createGitlabScmAuth(gitlabAuthApi),
    }),
});

const apiDocsConfigApi = ApiBlueprint.make({
  name: 'api-docs-config',
  params: defineParams =>
    defineParams({
      api: apiDocsConfigRef,
      deps: {},
      factory: () => {
        const definitionWidgets = defaultDefinitionWidgets();

        return {
          getApiDefinitionWidget: (apiEntity: ApiEntity) =>
            definitionWidgets.find(
              widget => widget.type === apiEntity.spec.type,
            ),
        };
      },
    }),
});

const gitlabCiApi = ApiBlueprint.make({
  name: 'gitlab-ci',
  params: defineParams =>
    defineParams({
      api: GitlabCIApiRef,
      deps: {
        configApi: configApiRef,
        discoveryApi: discoveryApiRef,
        gitlabAuthApi: gitlabAuthApiRef,
        identityApi: identityApiRef,
      },
      factory: ({ configApi, discoveryApi, gitlabAuthApi, identityApi }) =>
        GitlabCIClient.setupAPI({
          discoveryApi,
          identityApi,
          codeOwnersPath: configApi.getOptionalString(
            'gitlab.defaultCodeOwnersPath',
          ),
          readmePath: configApi.getOptionalString('gitlab.defaultReadmePath'),
          gitlabAuthApi,
          useOAuth: configApi.getOptionalBoolean('gitlab.useOAuth'),
          cache: {
            enabled: configApi.getOptionalBoolean('gitlab.cache.enabled'),
            ttl: configApi.getOptionalNumber('gitlab.cache.ttl'),
          },
        }),
    }),
});

const catalogApi = ApiBlueprint.make({
  name: 'catalog',
  params: defineParams =>
    defineParams({
      api: catalogApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new CatalogClient({ discoveryApi, fetchApi }),
    }),
});

const starredEntitiesApi = ApiBlueprint.make({
  name: 'catalog-starred-entities',
  params: defineParams =>
    defineParams({
      api: starredEntitiesApiRef,
      deps: {
        storageApi: storageApiRef,
      },
      factory: ({ storageApi }) =>
        new DefaultStarredEntitiesApi({ storageApi }),
    }),
});

const entityPresentationApi = ApiBlueprint.make({
  name: 'catalog-entity-presentation',
  params: defineParams =>
    defineParams({
      api: entityPresentationApiRef,
      deps: {
        catalogApi: catalogApiRef,
      },
      factory: ({ catalogApi: api }) =>
        DefaultEntityPresentationApi.create({ catalogApi: api }),
    }),
});

const kubernetesApi = ApiBlueprint.make({
  name: 'kubernetes',
  params: defineParams =>
    defineParams({
      api: kubernetesApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        kubernetesAuthProvidersApi: kubernetesAuthProvidersApiRef,
      },
      factory: ({ discoveryApi, fetchApi, kubernetesAuthProvidersApi }) =>
        new KubernetesBackendClient({
          discoveryApi,
          fetchApi,
          kubernetesAuthProvidersApi,
        }),
    }),
});

const kubernetesProxyApi = ApiBlueprint.make({
  name: 'kubernetes-proxy',
  params: defineParams =>
    defineParams({
      api: kubernetesProxyApiRef,
      deps: {
        kubernetesApi: kubernetesApiRef,
      },
      factory: ({ kubernetesApi: api }) =>
        new KubernetesProxyClient({ kubernetesApi: api }),
    }),
});

const kubernetesAuthProvidersApi = ApiBlueprint.make({
  name: 'kubernetes-auth-providers',
  params: defineParams =>
    defineParams({
      api: kubernetesAuthProvidersApiRef,
      deps: {
        gitlabAuthApi: gitlabAuthApiRef,
        googleAuthApi: googleAuthApiRef,
        microsoftAuthApi: microsoftAuthApiRef,
        oktaAuthApi: oktaAuthApiRef,
        oneloginAuthApi: oneloginAuthApiRef,
      },
      factory: ({
        gitlabAuthApi,
        googleAuthApi,
        microsoftAuthApi,
        oktaAuthApi,
        oneloginAuthApi,
      }) =>
        new KubernetesAuthProviders({
          microsoftAuthApi,
          googleAuthApi,
          oidcProviders: {
            gitlab: gitlabAuthApi,
            google: googleAuthApi,
            microsoft: microsoftAuthApi,
            okta: oktaAuthApi,
            onelogin: oneloginAuthApi,
          },
        }),
    }),
});

const kubernetesClusterLinkFormatterApi = ApiBlueprint.make({
  name: 'kubernetes-cluster-link-formatter',
  params: defineParams =>
    defineParams({
      api: kubernetesClusterLinkFormatterApiRef,
      deps: { googleAuthApi: googleAuthApiRef },
      factory: deps =>
        new KubernetesClusterLinkFormatter({
          formatters: getDefaultFormatters(deps),
          defaultFormatterName: DEFAULT_FORMATTER_NAME,
        }),
    }),
});

export const appModuleDrivelineApis = createFrontendModule({
  pluginId: 'app',
  extensions: [
    searchApi,
    analyticsApi,
    scmIntegrationsApi,
    scmAuthApi,
    apiDocsConfigApi,
    gitlabCiApi,
    catalogApi,
    starredEntitiesApi,
    entityPresentationApi,
    kubernetesApi,
    kubernetesProxyApi,
    kubernetesAuthProvidersApi,
    kubernetesClusterLinkFormatterApi,
  ],
});
